import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowDown, ArrowRight, Loader2, MoreVertical, Phone, Pin, Search, Upload, Video, X, AlertCircle } from "lucide-react";
import type { Message, Room } from "./types";
import { Avatar, Spinner, useChat } from "./ui";
import { call, uploadFile } from "./api";
import { getSocket } from "./socket";
import MessageBubble, { MsgAction } from "./MessageBubble";
import Composer, { SendPayload } from "./Composer";
import { ForwardDialog, HistoryDialog, Lightbox, ReadersDialog, RoomInfoDialog } from "./Dialogs";
import { dayKey, dayLabel, lastSeenText } from "./jalali";

export interface Jump { messageId?: number; replyTo?: Message; nonce: number; unread?: number }
interface Props { room: Room; jump: Jump; onBack: () => void; onSearch: (roomId: number) => void }
type Dlg = null | { k: "info" } | { k: "history"; id: number } | { k: "readers"; m: Message } | { k: "forward"; m: Message } | { k: "image"; m: Message };
interface Upl { id: string; name: string; pct: number; error?: string }

export default function ChatWindow({ room, jump, onBack, onSearch }: Props) {
  const { me, statusOf, socketUp, toast, ask, startCall, joinCall, openDirect, newTask, userById } = useChat();
  const roomId = room.id;
  const [msgs, setMsgs] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [hasAfter, setHasAfter] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [editing, setEditing] = useState<Message | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [uploads, setUploads] = useState<Upl[]>([]);
  const [typing, setTyping] = useState<Record<number, number>>({});
  const [dlg, setDlg] = useState<Dlg>(null);
  const [pins, setPins] = useState<Message[]>([]);
  const [pinIdx, setPinIdx] = useState(0);
  const [hl, setHl] = useState<number | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const [unreadFrom, setUnreadFrom] = useState<number | null>(null);
  const [drag, setDrag] = useState(false);

  const scroller = useRef<HTMLDivElement>(null);
  const msgsRef = useRef<Message[]>([]); msgsRef.current = msgs;
  const stick = useRef(true);
  const restore = useRef<{ h: number; top: number } | null>(null);
  const pendingScroll = useRef<{ id?: number; divider?: boolean } | null>(null);
  const lastTyping = useRef(0);
  const wasUp = useRef(socketUp);

  const amAdmin = room.type === "direct" || room.my_role !== "member";
  const blocked = room.type === "group" && room.only_admins_post && room.my_role === "member" ? "فقط ادمین‌ها می‌توانند در این گروه پیام بدهند" : null;
  const real = (list = msgsRef.current) => list.filter(m => !m._tmp);

  const scrollBottom = (smooth = false) => { const el = scroller.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" }); };
  const flash = (id: number) => { setHl(id); setTimeout(() => setHl(h => (h === id ? null : h)), 2200); };
  const markRead = useCallback(() => {
    if (document.hidden) return; const last = real().at(-1)?.id; if (last) call("mark_read", { room_id: roomId, upto_id: last });
  }, [roomId]); // eslint-disable-line
  const loadPins = useCallback(async () => { const r = await call("message_pins", { room_id: roomId }); if (r.success) { setPins(r.messages); setPinIdx(0); } }, [roomId]);

  const merge = (list: Message[]) => setMsgs(prev => {
    const map = new Map<number, Message>(prev.map(m => [m.id, m] as [number, Message])); let changed = false;
    for (const m of list) { const old = map.get(m.id); if (!old || JSON.stringify(old) !== JSON.stringify(m)) { map.set(m.id, m); changed = true; } }
    if (!changed) return prev;
    return Array.from(map.values()).sort((a, b) => (a._tmp ? 1 : 0) - (b._tmp ? 1 : 0) || a.id - b.id).filter((m, i, arr) => !(m._tmp && arr.some(x => !x._tmp && x.id === m.id)));
  });
  const syncLatest = useCallback(async () => {
    const lastId = real().at(-1)?.id;
    const r = await call("messages", { room_id: roomId, limit: 50 });
    if (r.success) { merge(r.messages); if (lastId && r.messages.some((m: Message) => m.id > lastId) && stick.current) setTimeout(() => scrollBottom(), 30); }
  }, [roomId]); // eslint-disable-line

  // ─── بارگذاری اولیه ───
  useEffect(() => {
    let dead = false;
    setLoading(true); setMsgs([]); setReplyTo(jump.replyTo ?? null); setEditing(null); setFiles([]); setPins([]); setTyping({}); setUnreadFrom(null); setNewCount(0); setDlg(null);
    (async () => {
      const myLast = room.members.find(m => m.id === me.id)?.last_read_id || 0;
      const r = await call("messages", { room_id: roomId, ...(jump.messageId ? { around_id: jump.messageId } : {}) });
      if (dead) return;
      if (r.success) {
        setMsgs(r.messages); setHasMore(r.has_more); setHasAfter(r.has_more_after);
        const fu = (r.messages as Message[]).find(m => m.id > myLast && m.sender_id !== me.id && m.type !== "system");
        const showDivider = !jump.messageId && (jump.unread ?? room.unread_count) > 0 && !!fu;
        setUnreadFrom(showDivider ? fu!.id : null);
        pendingScroll.current = jump.messageId ? { id: jump.messageId } : showDivider ? { divider: true } : {};
        stick.current = !jump.messageId && !showDivider;
      } else toast(r.message || "بارگذاری پیام‌ها ناموفق بود", false);
      setLoading(false); loadPins(); markRead();
    })();
    return () => { dead = true; };
  }, [roomId, jump.nonce]); // eslint-disable-line

  useLayoutEffect(() => {
    if (loading) return;
    const p = pendingScroll.current; if (!p) return; pendingScroll.current = null;
    if (p.id) { document.getElementById(`m-${p.id}`)?.scrollIntoView({ block: "center" }); flash(p.id); }
    else if (p.divider) document.getElementById("unread-divider")?.scrollIntoView({ block: "center" });
    else scrollBottom();
  }, [loading]);

  useLayoutEffect(() => {
    const el = scroller.current; if (!el) return;
    if (restore.current) { el.scrollTop = el.scrollHeight - restore.current.h + restore.current.top; restore.current = null; }
    else if (stick.current && !loading) el.scrollTop = el.scrollHeight;
  }, [msgs]); // eslint-disable-line

  // ─── رویدادهای زنده ───
  useEffect(() => {
    const s = getSocket(); if (!s) return;
    const onNew = ({ message }: { message: Message }) => {
      if (message.room_id !== roomId) return;
      setMsgs(prev => prev.some(m => m.id === message.id) ? prev : [...prev.filter(m => !(m._tmp && m.sender_id === message.sender_id && m.content === message.content && message.sender_id === me.id)), message]);
      if (message.sender_id !== me.id) { if (stick.current) markRead(); else setNewCount(c => c + 1); }
    };
    const onUpd = ({ message }: { message: Message }) => { if (message.room_id !== roomId) return; setMsgs(prev => prev.map(m => (m.id === message.id ? message : m))); loadPins(); };
    const onTyp = ({ room_id, user_id }: { room_id: number; user_id: number }) => { if (room_id === roomId) setTyping(t => ({ ...t, [user_id]: Date.now() + 4000 })); };
    s.on("message:new", onNew); s.on("message:updated", onUpd); s.on("typing", onTyp);
    return () => { s.off("message:new", onNew); s.off("message:updated", onUpd); s.off("typing", onTyp); };
  }, [roomId, socketUp]); // eslint-disable-line

  useEffect(() => { const t = setInterval(() => setTyping(p => { const n = Date.now(); const f = Object.fromEntries(Object.entries(p).filter(([, v]) => v > n)); return Object.keys(f).length === Object.keys(p).length ? p : f; }), 1500); return () => clearInterval(t); }, []);
  useEffect(() => { if (!socketUp) { const t = setInterval(syncLatest, 2500); return () => clearInterval(t); } }, [socketUp, syncLatest]);
  useEffect(() => { if (socketUp && !wasUp.current) syncLatest(); wasUp.current = socketUp; }, [socketUp, syncLatest]);
  useEffect(() => { const h = () => { if (!document.hidden) markRead(); }; document.addEventListener("visibilitychange", h); window.addEventListener("focus", h); return () => { document.removeEventListener("visibilitychange", h); window.removeEventListener("focus", h); }; }, [markRead]);

  // ─── اسکرول ───
  const onScroll = async () => {
    const el = scroller.current; if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    stick.current = dist < 120; setAtBottom(dist < 200); if (dist < 120 && newCount) { setNewCount(0); markRead(); }
    if (loadingMore || loading) return;
    if (el.scrollTop < 140 && hasMore) {
      const first = real()[0]?.id; if (!first) return;
      setLoadingMore(true); restore.current = { h: el.scrollHeight, top: el.scrollTop };
      const r = await call("messages", { room_id: roomId, before_id: first });
      if (r.success) { setHasMore(r.has_more); setMsgs(prev => [...r.messages, ...prev]); } else restore.current = null;
      setLoadingMore(false);
    } else if (dist < 160 && hasAfter) {
      const last = real().at(-1)?.id; if (!last) return;
      setLoadingMore(true);
      const r = await call("messages", { room_id: roomId, after_id: last });
      if (r.success) { setHasAfter(r.messages.length >= 300); merge(r.messages); }
      setLoadingMore(false);
    }
  };
  const goTo = async (id: number) => {
    if (document.getElementById(`m-${id}`)) { document.getElementById(`m-${id}`)?.scrollIntoView({ block: "center", behavior: "smooth" }); flash(id); return; }
    const r = await call("messages", { room_id: roomId, around_id: id });
    if (r.success) { stick.current = false; setMsgs(r.messages); setHasMore(r.has_more); setHasAfter(r.has_more_after); pendingScroll.current = { id }; setLoading(true); setTimeout(() => setLoading(false), 0); }
  };

  // ─── ارسال ───
  const makeTmp = (over: Partial<Message>): Message => ({
    id: -Date.now() - Math.floor(Math.random() * 1000), room_id: roomId, sender_id: me.id, sender_name: me.name, sender_avatar: me.avatar, type: "text", content: "",
    is_edited: 0, is_deleted: 0, is_pinned: 0, is_starred: 0, read_count: 0, created_at: new Date().toISOString(), mentions: [], call: null,
    reply: replyTo ? { id: replyTo.id, sender_name: replyTo.sender_name, content: replyTo.content, type: replyTo.type, is_deleted: replyTo.is_deleted, room_id: replyTo.room_id, room_name: null } : null,
    _pending: true, ...over,
  } as Message);
  const settle = (tmpId: number, real: Message) => setMsgs(prev => prev.some(m => m.id === real.id) ? prev.filter(m => m.id !== tmpId) : prev.map(m => (m.id === tmpId ? real : m)));
  const fail = (tmpId: number, msg?: string) => { setMsgs(prev => prev.map(m => (m.id === tmpId ? { ...m, _pending: false, _failed: true } : m))); toast(msg || "ارسال پیام ناموفق بود — روی علامت ! بزنید تا دوباره ارسال شود", false); };

  const sendText = async (text: string, mentions: number[], tmp?: Message) => {
    const t = tmp ?? makeTmp({ content: text });
    if (!tmp) { stick.current = true; setMsgs(p => [...p, t]); }
    else setMsgs(p => p.map(m => (m.id === t.id ? { ...m, _pending: true, _failed: false } : m)));
    const r = await call("message_send", { room_id: roomId, type: "text", content: text, reply_to: t.reply?.id, mentions });
    r.success ? settle(t.id, r.message) : fail(t.id, r.message);
  };
  const sendFile = async (file: File, caption: string, mentions: number[], withReply: boolean) => {
    const id = Math.random().toString(36).slice(2);
    const reply = withReply ? replyTo : null;
    setUploads(u => [...u, { id, name: file.name, pct: 0 }]);
    const up = await uploadFile(file, file.name, pct => setUploads(u => u.map(x => (x.id === id ? { ...x, pct } : x))));
    if (!up.success) { setUploads(u => u.map(x => (x.id === id ? { ...x, error: up.message || "آپلود ناموفق" } : x))); return; }
    const r = await call("message_send", { room_id: roomId, type: file.type.startsWith("image/") ? "image" : "file", content: caption || file.name, file_url: up.url, file_name: file.name, file_size: file.size, file_mime: up.mime || file.type, reply_to: reply?.id, mentions });
    setUploads(u => u.filter(x => x.id !== id));
    if (r.success) { stick.current = true; setMsgs(p => (p.some(m => m.id === r.message.id) ? p : [...p, r.message])); } else toast(r.message || "ارسال فایل ناموفق بود", false);
  };
  const onSend = async ({ text, mentions }: SendPayload) => {
    const batch = files; setFiles([]); const rep = replyTo; setReplyTo(null);
    if (batch.length) { for (let i = 0; i < batch.length; i++) await sendFile(batch[i], i === 0 ? text : "", i === 0 ? mentions : [], i === 0 && !!rep); }
    else await sendText(text, mentions, undefined);
  };
  const onVoice = async (blob: Blob, seconds: number, wave: number[]) => {
    const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
    const name = `voice_${Date.now()}.${ext}`; const id = Math.random().toString(36).slice(2);
    setUploads(u => [...u, { id, name: "پیام صوتی", pct: 0 }]);
    const up = await uploadFile(blob, name, pct => setUploads(u => u.map(x => (x.id === id ? { ...x, pct } : x))));
    if (!up.success) { setUploads(u => u.map(x => (x.id === id ? { ...x, error: up.message || "آپلود ناموفق" } : x))); return; }
    const r = await call("message_send", { room_id: roomId, type: "voice", content: "پیام صوتی", file_url: up.url, file_name: name, file_size: blob.size, file_mime: blob.type, duration: seconds, waveform: wave, reply_to: replyTo?.id });
    setUploads(u => u.filter(x => x.id !== id)); setReplyTo(null);
    if (r.success) { stick.current = true; setMsgs(p => (p.some(m => m.id === r.message.id) ? p : [...p, r.message])); } else toast(r.message || "ارسال ویس ناموفق بود", false);
  };
  const onEdit = async ({ text, mentions }: SendPayload) => {
    if (!editing) return; const m = editing; setEditing(null);
    if (text === m.content) return;
    const r = await call("message_edit", { message_id: m.id, content: text, mentions });
    if (r.success) setMsgs(p => p.map(x => (x.id === m.id ? r.message : x))); else toast(r.message || "ویرایش ناموفق بود", false);
  };
  const typingPing = () => { const n = Date.now(); if (n - lastTyping.current > 2500) { lastTyping.current = n; getSocket()?.emit("typing", { room_id: roomId }); } };

  const onAction = async (a: MsgAction, m: Message) => {
    switch (a) {
      case "reply": setReplyTo(m); setEditing(null); break;
      case "reply_private": await openDirect(m.sender_id, m); break;
      case "copy": navigator.clipboard?.writeText(m.content || ""); toast("متن کپی شد"); break;
      case "forward": setDlg({ k: "forward", m }); break;
      case "star": { const v = m.is_starred ? 0 : 1; setMsgs(p => p.map(x => (x.id === m.id ? { ...x, is_starred: v } : x))); call("message_star", { message_id: m.id, starred: v }); break; }
      case "pin": { const r = await call("message_pin", { message_id: m.id, pinned: m.is_pinned ? 0 : 1 }); if (!r.success) toast(r.message || "خطا", false); else { setMsgs(p => p.map(x => (x.id === m.id ? { ...x, is_pinned: m.is_pinned ? 0 : 1 } : x))); loadPins(); } break; }
      case "edit": setEditing(m); setReplyTo(null); break;
      case "delete":
        if (await ask({ title: "این پیام حذف شود؟", text: "پیام برای همه به‌صورت «حذف‌شده توسط شما» نمایش داده می‌شود و متن اصلی در سابقه می‌ماند.", confirm: "حذف پیام", danger: true })) {
          const r = await call("message_delete", { message_id: m.id }); if (!r.success) toast(r.message || "خطا", false); else syncLatest();
        } break;
      case "history": setDlg({ k: "history", id: m.id }); break;
      case "readers": if (m._failed) { const t = msgsRef.current.find(x => x.id === m.id); if (t) sendText(t.content || "", [], t); } else setDlg({ k: "readers", m }); break;
      case "task": newTask({ room_id: roomId, assignee_id: room.type === "direct" ? room.other_id || undefined : m.sender_id !== me.id ? m.sender_id : undefined, title: (m.content || "").slice(0, 100), description: m.content || "" }); break;
      case "jump": goTo(m.id); break;
      case "image": setDlg({ k: "image", m }); break;
      case "join_call": if (room.active_call) joinCall(room.active_call.id, roomId, room.active_call.type); break;
    }
  };

  const onDrop = (e: React.DragEvent) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files.length) setFiles([...files, ...Array.from(e.dataTransfer.files)]); };

  // ─── سربرگ ───
  const other = room.type === "direct" ? userById(room.other_id || 0) : undefined;
  const typingNames = Object.keys(typing).map(id => userById(+id)?.name).filter(Boolean) as string[];
  const st = room.other_id ? statusOf(room.other_id) : "offline";
  const sub = typingNames.length ? <span className="cx-typing">{typingNames.slice(0, 2).join("، ")} در حال تایپ<i>.</i><i>.</i><i>.</i></span>
    : room.type === "group" ? <span>{room.members.map(m => m.name.split(" ")[0]).slice(0, 5).join("، ")}{room.members.length > 5 ? ` و ${room.members.length - 5} نفر دیگر` : ""}</span>
    : st === "online" ? <span className="cx-on">آنلاین</span> : st === "idle" ? <span className="cx-idle">در سیستم — غیرفعال</span> : <span>آخرین بازدید: {lastSeenText(other?.last_seen)}</span>;
  const pin = pins[pinIdx];

  return (
    <section className="cx-chat" onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={e => e.currentTarget === e.target && setDrag(false)} onDrop={onDrop}>
      <header className="cx-chat-head">
        <button className="cx-icon-btn cx-back" onClick={onBack} aria-label="بازگشت"><ArrowRight size={21} /></button>
        <button className="cx-chat-title" onClick={() => setDlg({ k: "info" })}>
          <Avatar name={room.name} src={room.avatar} size={42} group={room.type === "group"} status={room.type === "direct" ? st : undefined} />
          <div><b>{room.name}</b><small>{sub}</small></div>
        </button>
        <div className="cx-head-actions">
          <button className="cx-icon-btn" onClick={() => onSearch(roomId)} title="جستجو در این گفتگو"><Search size={19} /></button>
          <button className="cx-icon-btn" onClick={() => startCall(roomId, "audio")} title="تماس صوتی" disabled={!!room.active_call}><Phone size={19} /></button>
          <button className="cx-icon-btn" onClick={() => startCall(roomId, "video")} title="تماس تصویری / کنفرانس" disabled={!!room.active_call}><Video size={20} /></button>
          <button className="cx-icon-btn" onClick={() => setDlg({ k: "info" })} title="اطلاعات"><MoreVertical size={19} /></button>
        </div>
      </header>

      {room.active_call && (
        <div className="cx-callbar"><span className="pulse" />{room.active_call.type === "video" ? "تماس تصویری" : "تماس صوتی"} در جریان است
          <button className="cx-btn sm primary" onClick={() => joinCall(room.active_call!.id, roomId, room.active_call!.type)}>پیوستن</button></div>
      )}
      {pin && (
        <button className="cx-pinbar" onClick={() => { goTo(pin.id); setPinIdx(i => (i + 1) % pins.length); }}>
          <Pin size={15} /><div><b>پیام پین‌شده{pins.length > 1 ? ` (${pinIdx + 1}/${pins.length})` : ""}</b><span>{pin.type === "text" ? pin.content : pin.type === "voice" ? "🎤 پیام صوتی" : pin.file_name}</span></div>
        </button>
      )}

      <div className="cx-scroll" ref={scroller} onScroll={onScroll} onLoadCapture={() => stick.current && scrollBottom()}>
        {loadingMore && <div className="cx-center sm"><Loader2 className="cx-spin" size={18} /></div>}
        {loading ? <div className="cx-center"><Spinner size={28} /></div> : msgs.length === 0 ? (
          <div className="cx-center cx-muted">هنوز پیامی نیست. اولین پیام را بفرستید 👋</div>
        ) : msgs.map((m, i) => {
          const prev = msgs[i - 1]; const newDay = !prev || dayKey(prev.created_at) !== dayKey(m.created_at);
          const head = !prev || newDay || prev.sender_id !== m.sender_id || prev.type === "system" || m.type === "system" || new Date(m.created_at).getTime() - new Date(prev.created_at).getTime() > 300000;
          return (
            <Fragment key={m.id}>
              {newDay && <div className="cx-day"><span>{dayLabel(m.created_at)}</span></div>}
              {unreadFrom === m.id && <div className="cx-unread" id="unread-divider"><span>پیام‌های خوانده‌نشده</span></div>}
              <MessageBubble msg={m} mine={m.sender_id === me.id} isGroup={room.type === "group"} showHead={head} members={room.members} amAdmin={amAdmin} highlight={hl === m.id} onAction={onAction} />
            </Fragment>
          );
        })}
      </div>

      {!atBottom && <button className="cx-fab" onClick={() => { stick.current = true; scrollBottom(true); setNewCount(0); markRead(); }}><ArrowDown size={19} />{newCount > 0 && <b>{newCount}</b>}</button>}

      {uploads.length > 0 && (
        <div className="cx-uploads">{uploads.map(u => (
          <div key={u.id} className={u.error ? "err" : ""}>
            {u.error ? <AlertCircle size={15} /> : <Upload size={15} />}<span>{u.name}</span>
            {u.error ? <><small>{u.error}</small><button className="cx-icon-btn" onClick={() => setUploads(x => x.filter(y => y.id !== u.id))}><X size={14} /></button></> : <><i style={{ width: `${u.pct}%` }} /><small>{u.pct}٪</small></>}
          </div>))}</div>
      )}

      <Composer room={room} members={room.members} replyTo={replyTo} editing={editing} blocked={blocked} files={files} setFiles={setFiles}
        onSend={onSend} onEdit={onEdit} onVoice={onVoice} onTyping={typingPing} onCancelReply={() => setReplyTo(null)} onCancelEdit={() => setEditing(null)} />

      {drag && <div className="cx-drop"><Upload size={34} /><b>فایل را رها کنید تا ضمیمه شود</b></div>}
      {dlg?.k === "info" && <RoomInfoDialog room={room} onClose={() => setDlg(null)} />}
      {dlg?.k === "history" && <HistoryDialog messageId={dlg.id} onClose={() => setDlg(null)} />}
      {dlg?.k === "readers" && <ReadersDialog message={dlg.m} onClose={() => setDlg(null)} />}
      {dlg?.k === "forward" && <ForwardDialog message={dlg.m} onClose={() => setDlg(null)} />}
      {dlg?.k === "image" && <Lightbox msg={dlg.m} onClose={() => setDlg(null)} />}
    </section>
  );
}
